import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { X } from 'lucide-react';
import { cn } from '@utils/cn';
import { Z_INDEX } from '@constants/zIndex';
import { toast } from 'react-toastify';
import ConstructionOutlined from '@mui/icons-material/ConstructionOutlined';
import { Modal } from '@components/molecules/Modal';
import { Button } from '@components/atoms/Button';
import { Input } from '@components/atoms/Input';
import { TextareaInput } from '@components/atoms/TextareaInput';
import { InvoicePreviewDrawer } from '@components/organisms/InvoicePreviewDrawer';
import { ConnectPaymentAccountModal } from '@components/organisms/ConnectPaymentAccountModal';
import { useMyPaymentAccount } from '@hooks/api/merchantPaymentAccount';
import { useMyInvoicingPaused } from '@hooks/api/organizations';
import {
  useCreateInvoiceDraft,
  useDeleteInvoiceDraft,
  useGetInvoicesByProject,
  useRefreshInvoiceStatus,
  useResendInvoiceEmail,
  useSendInvoice,
  useUpdateInvoiceDraft,
  useVoidInvoice,
} from '@hooks/api/invoice';
import { Invoice, InvoiceStatus } from '@models/invoice.model';
import { ActiveDraftView } from './components/ActiveDraftView';
import { DraftsList } from './components/DraftsList';
import { PaidList } from './components/PaidList';
import { SentList } from './components/SentList';
import { useTranslation } from '@hooks/useTranslation';

type TabKey = 'drafts' | 'sent' | 'paid';

interface InvoiceManagerSliderProps {
  isOpen: boolean;
  projectId: string;
  projectName?: string;
  defaultRecipientEmail?: string;
  onClose: () => void;
}

const DRAFT_STATUSES: InvoiceStatus[] = [
  InvoiceStatus.DRAFT,
  InvoiceStatus.SEND_FAILED,
  InvoiceStatus.SENDING,
];
const SENT_STATUSES: InvoiceStatus[] = [InvoiceStatus.SENT, InvoiceStatus.VOIDED];
const PAID_STATUSES: InvoiceStatus[] = [InvoiceStatus.PAID];

export const InvoiceManagerSlider: React.FC<InvoiceManagerSliderProps> = ({
  isOpen,
  projectId,
  projectName,
  defaultRecipientEmail,
  onClose,
}) => {
  const tActionMessages = useTranslation('actionMessages');
  const organizationName = useSelector(
    (state: any) => state?.organization?.name as string | null | undefined,
  );

  const paymentAccount = useMyPaymentAccount();
  const { data: invoicingPaused } = useMyInvoicingPaused();
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showPausedModal, setShowPausedModal] = useState(false);

  const [activeTab, setActiveTab] = useState<TabKey>('drafts');
  const [activeInvoiceId, setActiveInvoiceId] = useState<string | null>(null);
  const [previewInvoice, setPreviewInvoice] = useState<Invoice | null>(null);
  const [voidingInvoice, setVoidingInvoice] = useState<Invoice | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [resendingInvoice, setResendingInvoice] = useState<Invoice | null>(null);
  const [resendEmailValue, setResendEmailValue] = useState('');
  const [resendEmailError, setResendEmailError] = useState<string | null>(null);
  const [emailPromptValue, setEmailPromptValue] = useState(defaultRecipientEmail || '');
  const [emailValidationError, setEmailValidationError] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);

  const { data: allInvoices = [] } = useGetInvoicesByProject(isOpen ? projectId : null);
  const createDraft = useCreateInvoiceDraft();
  const updateDraft = useUpdateInvoiceDraft();
  const deleteDraft = useDeleteInvoiceDraft();
  const sendInvoice = useSendInvoice();
  const voidInvoice = useVoidInvoice();
  const resendInvoice = useResendInvoiceEmail();
  const refreshStatus = useRefreshInvoiceStatus();
  // Tracks the per-open-cycle set of SENT invoices we've already reconciled against
  // NMI, so the fan-out below doesn't re-fire every render. Cleared when the slider
  // closes so the next time the user opens it we re-check anything still SENT.
  const reconciledIdsRef = useRef<Set<string>>(new Set());

  const drafts = useMemo(
    () => allInvoices.filter((inv) => DRAFT_STATUSES.includes(inv.status)),
    [allInvoices],
  );
  const sent = useMemo(
    () => allInvoices.filter((inv) => SENT_STATUSES.includes(inv.status)),
    [allInvoices],
  );
  const paid = useMemo(
    () => allInvoices.filter((inv) => PAID_STATUSES.includes(inv.status)),
    [allInvoices],
  );

  useEffect(() => {
    if (!isOpen) {
      setActiveTab('drafts');
      setActiveInvoiceId(null);
      setPreviewInvoice(null);
      setCreateError(null);
      setEmailValidationError(null);
      setEmailPromptValue(defaultRecipientEmail || '');
      setVoidingInvoice(null);
      setVoidReason('');
      setResendingInvoice(null);
      setResendEmailValue('');
      setResendEmailError(null);
      setShowPausedModal(false);
      reconciledIdsRef.current.clear();
    }
  }, [isOpen, defaultRecipientEmail]);

  // Silently fan out a per-invoice NMI status refresh for every SENT invoice in the
  // current list. Each ID is only fired once per open cycle (tracked above) so we
  // don't re-hit NMI on every cache invalidation. When NMI reports paid/voided the
  // mutation invalidates the list cache and the badge updates on its own.
  useEffect(() => {
    if (!isOpen) return;
    for (const invoice of sent) {
      if (
        invoice.status === InvoiceStatus.SENT &&
        !reconciledIdsRef.current.has(invoice.invoiceId)
      ) {
        reconciledIdsRef.current.add(invoice.invoiceId);
        refreshStatus.mutate(invoice.invoiceId);
      }
    }
  }, [isOpen, sent, refreshStatus]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !previewInvoice) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose, previewInvoice]);

  const performCreate = (recipientEmail?: string) => {
    setCreateError(null);
    createDraft.mutate(
      { projectId, recipientEmail: recipientEmail || defaultRecipientEmail || undefined },
      {
        onSuccess: (invoice) => {
          setActiveInvoiceId(invoice.invoiceId);
          setEmailValidationError(null);
          setEmailPromptValue(defaultRecipientEmail || '');
        },
        onError: (error: any) => {
          const message =
            error?.response?.errors?.[0]?.message || error?.message || 'Failed to create draft';
          setCreateError(message);
        },
      },
    );
  };

  const handleSubmitEmailPrompt = () => {
    const trimmed = emailPromptValue.trim();
    if (!trimmed) {
      setEmailValidationError('Please enter a recipient email to start the draft.');
      return;
    }
    setEmailValidationError(null);
    performCreate(trimmed);
  };

  const handleDeleteActiveDraft = () => {
    if (!activeInvoiceId) return;
    deleteDraft.mutate(activeInvoiceId, {
      onSuccess: () => setActiveInvoiceId(null),
    });
  };

  const handleSavePreviewDescription = async (description: string) => {
    if (!previewInvoice) return;
    try {
      const updated = await updateDraft.mutateAsync({
        invoiceId: previewInvoice.invoiceId,
        expectedVersion: previewInvoice.version,
        orderDescription: description,
      });
      setPreviewInvoice({
        ...previewInvoice,
        orderDescription: updated.orderDescription,
        version: updated.version,
      });
    } catch {
      // toast surfaced by hook
    }
  };

  const handleSend = async () => {
    if (!previewInvoice) return;

    // Just-in-time payment-onboarding gate: if the org hasn't connected an NMI
    // merchant account yet, block the send and open the connect modal instead.
    // While the account query is still loading, refetch and await it so we don't
    // gate on a stale/empty cache. `null` data = not connected.
    let account = paymentAccount.data;
    if (!account && paymentAccount.isLoading) {
      try {
        const result = await paymentAccount.refetch();
        account = result.data;
      } catch {
        // fall through — treated as not connected below
      }
    }
    if (!account) {
      toast.info(tActionMessages.connectPaymentAccountToSend);
      setShowConnectModal(true);
      return;
    }

    // Invoicing is globally paused by an admin (payment-provider maintenance). The org
    // has keys, so instead of letting the NMI call fail we surface the paused message at
    // this final step and stop the send. The rest of the UI stays fully usable.
    if (invoicingPaused) {
      setShowPausedModal(true);
      return;
    }

    try {
      await sendInvoice.mutateAsync(previewInvoice.invoiceId);
      setPreviewInvoice(null);
      setActiveInvoiceId(null);
      setActiveTab('sent');
    } catch {
      // toast surfaced by hook
    }
  };

  const handleOpenSentOrPaid = (invoiceId: string) => {
    const invoice = allInvoices.find((inv) => inv.invoiceId === invoiceId);
    if (!invoice) return;
    setPreviewInvoice(invoice);
    if (invoice.status !== InvoiceStatus.SENT) return;
    // Reconcile silently — if NMI reports the invoice paid/voided, swap the open
    // drawer's invoice in place so the badge and status copy update immediately.
    refreshStatus.mutate(invoiceId, {
      onSuccess: (refreshed) => {
        if (!refreshed) return;
        setPreviewInvoice((current) =>
          current && current.invoiceId === refreshed.invoiceId ? refreshed : current,
        );
      },
    });
  };

  // Voiding closes the invoice in the payment provider, so the pause blocks it
  // server-side too. Intercept here, or the user gets a raw error toast instead of the
  // paused modal.
  const handleStartVoid = (invoice: Invoice) => {
    if (invoicingPaused) {
      setShowPausedModal(true);
      return;
    }
    setVoidingInvoice(invoice);
  };

  const handleCloseVoid = () => {
    setVoidingInvoice(null);
    setVoidReason('');
  };

  const handleConfirmVoid = () => {
    if (!voidingInvoice) return;
    const trimmedReason = voidReason.trim();
    voidInvoice.mutate(
      {
        invoiceId: voidingInvoice.invoiceId,
        reason: trimmedReason || undefined,
      },
      {
        onSuccess: () => handleCloseVoid(),
      },
    );
  };

  const handleStartResend = async (invoice: Invoice) => {
    // Resending reaches the payment provider, so the pause blocks it server-side too.
    if (invoicingPaused) {
      setShowPausedModal(true);
      return;
    }

    // Same just-in-time payment-onboarding gate as handleSend: resending re-sends the
    // invoice email via NMI and needs the org's connected merchant key. If absent, open
    // the connect modal instead of letting the resend fail server-side.
    let account = paymentAccount.data;
    if (!account && paymentAccount.isLoading) {
      try {
        const result = await paymentAccount.refetch();
        account = result.data;
      } catch {
        // fall through — treated as not connected below
      }
    }
    if (!account) {
      toast.info(tActionMessages.connectPaymentAccountToResend);
      setShowConnectModal(true);
      return;
    }

    setResendEmailValue(invoice.recipientEmail || '');
    setResendEmailError(null);
    setResendingInvoice(invoice);
  };

  const handleCloseResend = () => {
    setResendingInvoice(null);
    setResendEmailValue('');
    setResendEmailError(null);
  };

  const handleConfirmResend = () => {
    if (!resendingInvoice) return;
    const trimmed = resendEmailValue.trim();
    if (!trimmed) {
      setResendEmailError('Please enter a recipient email.');
      return;
    }
    resendInvoice.mutate(
      { invoiceId: resendingInvoice.invoiceId, recipientEmail: trimmed },
      {
        onSuccess: () => handleCloseResend(),
      },
    );
  };

  if (!isOpen) return null;

  const showActiveDraft = activeTab === 'drafts' && activeInvoiceId !== null;

  const tabs: Array<{ key: TabKey; label: string; count: number }> = [
    { key: 'drafts', label: 'Drafts', count: drafts.length },
    { key: 'sent', label: 'Sent', count: sent.length },
    { key: 'paid', label: 'Paid', count: paid.length },
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className="fixed inset-0 bg-black/40 transition-opacity duration-300"
        style={{ zIndex: Z_INDEX.SLIDE_OVER_BACKDROP }}
      />

      {/* Slider panel */}
      <div
        className="fixed right-0 top-0 bottom-0 w-full sm:w-[85%] lg:w-[78%] bg-white shadow-2xl flex flex-col"
        style={{ zIndex: Z_INDEX.SLIDE_OVER_CONTENT }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="min-w-0">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Project</p>
            <h2 className="text-base font-semibold text-gray-900 truncate">
              {projectName ? `${projectName} · Invoices` : 'Invoices'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close invoices"
            className="p-1.5 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 px-6 flex-shrink-0">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => {
                setActiveTab(tab.key);
                setActiveInvoiceId(null);
                setCreateError(null);
                setEmailValidationError(null);
              }}
              className={cn(
                'px-3 py-3 text-sm',
                activeTab === tab.key
                  ? 'font-medium text-blue-800 border-b-2 border-blue-600 -mb-px'
                  : 'text-gray-500 hover:text-gray-700',
              )}>
              {tab.label}
              <span className="ml-1 text-xs text-gray-400">({tab.count})</span>
            </button>
          ))}
        </div>

        {/* Recipient row — the single entry point to create a new draft.
            Always visible on Drafts tab while not editing an existing draft. */}
        {activeTab === 'drafts' && !showActiveDraft && (
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <p className="text-sm font-medium text-gray-900 mb-1">Recipient email</p>
            <p className="text-xs text-gray-600 mb-3">
              {createError ||
                "We'll send this invoice to the email below. You can change it before starting the draft."}
            </p>
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <Input
                  type="email"
                  value={emailPromptValue}
                  error={Boolean(emailValidationError)}
                  onChange={(e) => {
                    setEmailPromptValue(e.target.value);
                    if (emailValidationError) setEmailValidationError(null);
                    if (createError) setCreateError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSubmitEmailPrompt();
                  }}
                  placeholder="invoices@client.com"
                />
              </div>
              <button
                type="button"
                onClick={handleSubmitEmailPrompt}
                disabled={createDraft.isPending}
                className="px-4 py-2 rounded text-sm font-medium text-white bg-blue-900 hover:bg-blue-800 disabled:opacity-50">
                {createDraft.isPending ? 'Creating…' : 'Start draft'}
              </button>
            </div>
            {emailValidationError && (
              <p className="text-xs text-red-600 mt-2">{emailValidationError}</p>
            )}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {showActiveDraft && activeInvoiceId ? (
            <ActiveDraftView
              invoiceId={activeInvoiceId}
              projectId={projectId}
              onBack={() => setActiveInvoiceId(null)}
              onReviewAndSend={(invoice) => setPreviewInvoice(invoice)}
              onDelete={handleDeleteActiveDraft}
              isDeleting={deleteDraft.isPending}
            />
          ) : activeTab === 'drafts' ? (
            <DraftsList
              drafts={drafts}
              onOpenDraft={(id) => setActiveInvoiceId(id)}
            />
          ) : activeTab === 'sent' ? (
            <SentList
              invoices={sent}
              onOpenInvoice={handleOpenSentOrPaid}
              onVoidInvoice={handleStartVoid}
              onResendInvoice={handleStartResend}
            />
          ) : (
            <PaidList invoices={paid} onOpenInvoice={handleOpenSentOrPaid} />
          )}
        </div>
      </div>

      {/* Nested preview drawer */}
      <InvoicePreviewDrawer
        isOpen={previewInvoice !== null}
        invoice={previewInvoice}
        organizationName={organizationName || undefined}
        onClose={() => setPreviewInvoice(null)}
        onSend={handleSend}
        isSending={sendInvoice.isPending}
        onSaveDescription={handleSavePreviewDescription}
      />

      {/* Void confirmation */}
      <Modal
        isOpen={voidingInvoice !== null}
        onClose={handleCloseVoid}
        title={`Void invoice ${voidingInvoice?.invoiceNumber || ''}`}
        description={
          voidingInvoice
            ? `This will mark invoice ${voidingInvoice.invoiceNumber || voidingInvoice.invoiceId.slice(0, 8)} as voided and close it in NMI. The budget items charged here will be released so you can include them in a new invoice. The recipient will be notified by email. This action cannot be undone.`
            : ''
        }
        size="small"
        handleConfirm={handleConfirmVoid}
        confirmText={voidInvoice.isPending ? 'Voiding…' : 'Void invoice'}
        cancelText="Cancel"
        isLoading={voidInvoice.isPending}>
        <div className="flex flex-col gap-2">
          <label className="text-xs uppercase tracking-wide text-gray-500" htmlFor="void-reason">
            Reason <span className="text-gray-400 normal-case">(optional)</span>
          </label>
          <TextareaInput
            id="void-reason"
            value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)}
            placeholder="e.g., Created in error, client cancelled order, billing dispute…"
            rows={3}
            resize="none"
          />
          <p className="text-xs text-gray-500">
            If provided, this reason will be shown in the recipient's notification email and saved
            with the status history.
          </p>
        </div>
      </Modal>

      {/* Resend invoice email */}
      <Modal
        isOpen={resendingInvoice !== null}
        onClose={handleCloseResend}
        title={`Resend invoice ${resendingInvoice?.invoiceNumber || ''}`}
        description="The customer will receive the invoice email again at the address below. You can change the recipient if needed — the change will be saved to the invoice."
        size="small"
        handleConfirm={handleConfirmResend}
        confirmText={resendInvoice.isPending ? 'Sending…' : 'Resend'}
        cancelText="Cancel"
        isLoading={resendInvoice.isPending}>
        <div className="flex flex-col gap-2">
          <label className="text-xs uppercase tracking-wide text-gray-500" htmlFor="resend-recipient-email">
            Recipient email
          </label>
          <Input
            id="resend-recipient-email"
            type="email"
            value={resendEmailValue}
            error={Boolean(resendEmailError)}
            onChange={(e) => {
              setResendEmailValue(e.target.value);
              if (resendEmailError) setResendEmailError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleConfirmResend();
            }}
            placeholder="invoices@client.com"
          />
          {resendEmailError && (
            <p className="text-xs text-red-600">{resendEmailError}</p>
          )}
        </div>
      </Modal>

      {/* Invoicing-paused popup — shown at the final review/send step when an admin
          has paused invoicing. Portaled to <body> at the top layer so it renders
          above the review drawer (a body-portaled secondary slide-over). Informational
          only; the invoice stays a draft. */}
      {showPausedModal &&
        createPortal(
          <div
            className="fixed inset-0 flex items-center justify-center p-4"
            style={{ zIndex: Z_INDEX.EMERGENCY_MODAL }}>
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setShowPausedModal(false)}
            />
            <div className="relative flex w-full max-w-md flex-col items-center gap-5 rounded-xl bg-white p-6 text-center shadow-2xl">
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-orange-100 text-orange-500">
                <ConstructionOutlined sx={{ fontSize: 40 }} />
              </div>
              <div className="flex flex-col items-center gap-2">
                <h2 className="text-xl font-bold text-gray-900">
                  Invoicing temporarily unavailable
                </h2>
                <p className="text-sm leading-relaxed text-gray-500">
                  Our payment provider is undergoing maintenance, so invoices can&apos;t
                  be sent, resent or voided right now. Your work is saved — please try
                  again soon.
                </p>
                <span className="mt-1 inline-flex items-center gap-2 rounded-full bg-orange-50 px-3 py-1 text-xs font-medium text-orange-600">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-orange-500" />
                  Back soon
                </span>
              </div>
              <Button variant="primary" onClick={() => setShowPausedModal(false)}>
                Got it
              </Button>
            </div>
          </div>,
          document.body,
        )}

      {/* Just-in-time payment onboarding gate. After a successful connect, the
          myPaymentAccount cache holds the account, so re-entering handleSend
          passes the gate and actually sends — exactly once, no loop. */}
      <ConnectPaymentAccountModal
        open={showConnectModal}
        onClose={() => setShowConnectModal(false)}
        onConnected={() => {
          setShowConnectModal(false);
          void handleSend();
        }}
      />
    </>
  );
};
